/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { CircularProgress, Tooltip } from '@mui/material';

import { ClusterLifecycleStates, ClusterStates } from 'shared';

import { SimpleConfirmModal } from '../../../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleErrorModal } from '../../../../../../components/SimpleErrorModal/SimpleErrorModal';
import { HUChip } from '../../../../../../elements/HUChip/HUChip';
import { Namespaces } from '../../../../../../i18n';
import { ClusterApiService } from '../../../../../../services/ClusterApiService';
import { ApiClientError } from '../../../../../../types/api/ApiClientError';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { determineStatusColor } from '../../../../../../utils/determineStatusColor';
import { reactToUpdateCluster } from '../../../../../../utils/reactions';
import { useObjectState } from '../../../../../../utils/useObjectState';
import { createServiceStateControlChipStyles } from './ServiceStateControlChip.styles';

type ServiceStateControlChipProps = {
  status: ClusterStates;
  identificationData: UpdateClusterDetailsParameterIdentification;
};

type ModalState = {
  lifecycleModal: { isOpen: boolean; message: string };
  errorModal: { isOpen: boolean; message: string };
};

export const ServiceStateControlChip = memo<ServiceStateControlChipProps>(props => {
  const { status, identificationData } = props;

  const { t } = useTranslation();

  const classes = createServiceStateControlChipStyles();

  const [isProcessingStateUpdate, setIsProcessingStateUpdate] = useState(false);
  const [modalState, setModalState] = useObjectState<ModalState>({
    lifecycleModal: { isOpen: false, message: '' },
    errorModal: { isOpen: false, message: '' },
  });

  const showUpdateStateAction = useMemo(
    () => status === ClusterStates.Running || status === ClusterStates.Stopped,
    [status],
  );

  useEffect(() => {
    if (!showUpdateStateAction) {
      setTimeout(() => {
        setIsProcessingStateUpdate(false);
      }, 1_000);
    }
  }, [showUpdateStateAction]);

  const handleLifecycleModalOpen = () => {
    const message =
      status === ClusterStates.Running
        ? t('cluster:details.lifecycle_state.stop_confirm_message')
        : t('cluster:details.lifecycle_state.start_confirm_message');
    setModalState({ lifecycleModal: { isOpen: true, message } });
  };

  const handleLifecycleActionConfirm = useCallback(async () => {
    setTimeout(async () => {
      setIsProcessingStateUpdate(true);

      try {
        await ClusterApiService.updateClusterLifecycleState({
          ...identificationData,
          data: {
            state: status === ClusterStates.Running ? ClusterLifecycleStates.Stopping : ClusterLifecycleStates.Starting,
          },
        });
        await reactToUpdateCluster({ ...identificationData });
      } catch (e) {
        const error = e as ApiClientError;
        setIsProcessingStateUpdate(false);
        setModalState({ errorModal: { isOpen: true, message: error.message } });
      }
    }, 400);
  }, [identificationData, status, setModalState]);

  return (
    <>
      <HUChip
        color={determineStatusColor(status)}
        className={classes.chipStatus}
        classes={{ deleteIcon: classes.chipStopIcon }}
        label={t(`${Namespaces.Entities}:cluster.k8s_status.values.${status.toLowerCase()}`)}
        data-cy="cluster-header-status"
        deleteIcon={
          <Tooltip
            title={
              status === ClusterStates.Running
                ? t('cluster:details.lifecycle_state.stop_tooltip')
                : t('cluster:details.lifecycle_state.start_tooltip')
            }
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                transition: 'width 0.3s ease-out 0.3s, opacity 0.3s ease-out 0.3s, margin 0.3s ease-out 0.3s',
                width: showUpdateStateAction ? '24px' : '0px',
                opacity: showUpdateStateAction ? 1 : 0,
                marginRight: showUpdateStateAction ? '5px' : '0px',
                marginLeft: showUpdateStateAction ? '-6px' : '0px',
                cursor: isProcessingStateUpdate ? 'default' : 'pointer',
                pointerEvents: isProcessingStateUpdate ? 'none' : 'auto',
              }}
              data-cy="cluster-header-status-lifecycle-action"
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  transition: 'opacity 0.3s ease-out 0.2s',
                  opacity: isProcessingStateUpdate ? 1 : 0,
                }}
              >
                <CircularProgress sx={{ color: 'white' }} size="18px" thickness={4.5} />
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'opacity 0.3s ease-out',
                  opacity: isProcessingStateUpdate ? 0 : 1,
                }}
              >
                {status === ClusterStates.Running ? <PauseCircleIcon /> : <PlayCircleIcon />}
              </div>
            </div>
          </Tooltip>
        }
        onDelete={handleLifecycleModalOpen}
      />
      <SimpleConfirmModal
        open={modalState.lifecycleModal.isOpen}
        message={modalState.lifecycleModal.message}
        onCancel={() => setModalState({ lifecycleModal: { isOpen: false, message: '' } })}
        onConfirm={() => {
          setModalState({ lifecycleModal: { isOpen: false, message: '' } });
          void handleLifecycleActionConfirm();
        }}
      />
      <SimpleErrorModal
        open={modalState.errorModal.isOpen}
        message={modalState.errorModal.message}
        onOk={() => setModalState({ errorModal: { isOpen: false, message: '' } })}
      />
    </>
  );
});
